import { queryClientDefaultOptions } from "@dc/utils";
import { isServer, MutationCache, QueryClient } from "@tanstack/react-query";
import { cache } from "react";

// Query URLs that should not be invalidated by global mutation success
const EXCLUDED_QUERY_URLS = ["/api/v1/files/:id/nutrient-config"];

const INVALIDATION_DEBOUNCE_MS = 500;
const INVALIDATION_MAX_WAIT_MS = 2_000;

function makeQueryClient() {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
  let activeBatch = 0;

  function flushInvalidation(batch: number) {
    if (batch !== activeBatch) return;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (maxWaitTimer) {
      clearTimeout(maxWaitTimer);
      maxWaitTimer = null;
    }
    activeBatch++;
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if (Array.isArray(queryKey) && queryKey.length > 0) {
          const firstKey = queryKey[0];
          if (typeof firstKey === "object" && firstKey !== null && "url" in firstKey) {
            const url = (firstKey as { url: string }).url;
            if (EXCLUDED_QUERY_URLS.includes(url)) {
              return false;
            }
          }
        }
        return true;
      },
    });
  }

  function scheduleInvalidation() {
    const batch = activeBatch;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => flushInvalidation(batch), INVALIDATION_DEBOUNCE_MS);
    if (!maxWaitTimer) {
      maxWaitTimer = setTimeout(() => flushInvalidation(batch), INVALIDATION_MAX_WAIT_MS);
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: queryClientDefaultOptions.queries,
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        if (mutation.meta?.skipInvalidation) return;
        if (mutation.meta?.invalidateImmediately) {
          flushInvalidation(activeBatch);
          return;
        }
        scheduleInvalidation();
      },
    }),
  });
  return queryClient;
}

let browserQueryClient: QueryClient | null = null;

export const getQueryClient = cache(() => {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }
    return browserQueryClient;
  }
});
